import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RootState } from 'app/store';
import { getData } from 'features/dinamic';
import { setLoading } from 'features/loader';
import { setDataToast, setVisibleToast } from 'features/toast';
import { selectToken } from 'features/auth';
import { getTokenPayload, extractUsuarioFromPayload, extractIdUsuarioFromPayload } from 'helpers/token';

interface PendingSolicitudInfo {
  hasActive: boolean;
  totalPendientes: number;
  totalAprobadas: number;
  totalRechazadas: number;
  hasCiclo: boolean;
  hasGrupo: boolean;
  detalle?: {
    idSolicitud: number;
    tipo: 'INSCRIPCION' | 'REINSCRIPCION';
    estado: string;
    idDiplomado: number;
    idCicloDeseado: number | null;
    fechaCreacion: string;
  } | null;
}

interface SolicitudState {
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
  diplomados: any[];
  totalDiplomados: number;
  ciclos: any[];
  totalCiclos: number;
  creating: boolean;
  lastMessage: string | null;
  pendingSolicitud: PendingSolicitudInfo | null;
  checkingPending: boolean;
  dataCompletion: any | null;
  checkingCompletion: boolean;
}

const initialState: SolicitudState = {
  status: 'idle',
  error: null,
  diplomados: [],
  totalDiplomados: 0,
  ciclos: [],
  totalCiclos: 0,
  creating: false,
  lastMessage: null,
  pendingSolicitud: null,
  checkingPending: false,
  dataCompletion: null,
  checkingCompletion: false,
};

const resolveAuth = (state: RootState) => {
  const token = selectToken(state);
  const payload = getTokenPayload(token);
  return {
    usuario: extractUsuarioFromPayload(payload),
    idUsuario: extractIdUsuarioFromPayload(payload),
  };
};

const handleToast = (dispatch: any, title: string, message: string, type: 'LimeGreen' | 'Crimson') => {
  dispatch(setDataToast({ title, message, data: null, type, time: Date.now() }));
  dispatch(setVisibleToast(true));
};

export const fetchPublicDiplomados = createAsyncThunk(
  'solicitud/fetchPublicDiplomados',
  async ({ page = 1, pageSize = 10, q, edad }: { page?: number; pageSize?: number; q?: string; edad?: number }, { dispatch, getState, rejectWithValue }) => {
    const body: any = { idsp: 302, page, pageSize };
    if (q) body.q = q;
    if (edad) body.edad = edad;

    dispatch(setLoading(true));
    const { result, message, data } = await getData(body, getState, dispatch);
    dispatch(setLoading(false));

    if (result) {
      const parsed = typeof data === 'string' ? JSON.parse(data) : data;
      const items = Array.isArray(parsed) ? parsed : parsed?.items || [];
      const total = parsed?.total ?? items.length;
      return { items, total };
    }

    return rejectWithValue(message || 'No fue posible obtener los diplomados');
  }
);

export const fetchPublicCiclos = createAsyncThunk(
  'solicitud/fetchPublicCiclos',
  async ({ idDiplomado, page = 1, pageSize = 10 }: { idDiplomado: number; page?: number; pageSize?: number }, { dispatch, getState, rejectWithValue }) => {
    dispatch(setLoading(true));
    const { result, message, data } = await getData({ idsp: 303, idDiplomado, page, pageSize }, getState, dispatch);
    dispatch(setLoading(false));

    if (result) {
      const parsed = typeof data === 'string' ? JSON.parse(data) : data;
      const items = Array.isArray(parsed) ? parsed : parsed?.items || [];
      const total = parsed?.total ?? items.length;
      return { items, total };
    }

    return rejectWithValue(message || 'No fue posible obtener los ciclos');
  }
);

interface CreateSolicitudPayload {
  idDiplomado: number;
  idCicloDeseado?: number | null;
}

export const createSolicitudInscripcion = createAsyncThunk(
  'solicitud/createSolicitudInscripcion',
  async ({ idDiplomado, idCicloDeseado }: CreateSolicitudPayload, { dispatch, getState, rejectWithValue }) => {

    const body = {
      idsp: 304,
      idDiplomado,
      idCicloDeseado: idCicloDeseado || null,
    };

    dispatch(setLoading(true));
    const { result, message, data } = await getData(body, getState, dispatch);
    dispatch(setLoading(false));

    if (result) {
      handleToast(dispatch, 'Solicitud enviada', 'Tu solicitud ha sido registrada y está en revisión.', 'LimeGreen');
      return { message, data };
    }

    handleToast(dispatch, 'Acción incompleta', message || 'No fue posible registrar la solicitud', 'Crimson');
    return rejectWithValue(message || data || 'Error');
  }
);

export const createSolicitudReinscripcion = createAsyncThunk(
  'solicitud/createSolicitudReinscripcion',
  async ({ idDiplomado, idCiclo }: { idDiplomado: number; idCiclo: number | null }, { dispatch, getState, rejectWithValue }) => {
    const state = getState() as RootState;
    const { usuario } = resolveAuth(state);
    const usuarioFinal = usuario || state.alumnoProfile.usuario;
    if (!usuarioFinal) {
      return rejectWithValue('Usuario no identificado');
    }

    const body = {
      idsp: 223,
      idDiplomado,
      idCicloDeseado: idCiclo,
    };

    dispatch(setLoading(true));
    const { result, message, data } = await getData(body, getState, dispatch);
    dispatch(setLoading(false));

    if (result) {
      handleToast(dispatch, 'Solicitud enviada', 'Tu solicitud de reinscripción ha sido registrada.', 'LimeGreen');
      return { message, data };
    }

    handleToast(dispatch, 'Acción incompleta', message || 'No fue posible registrar la solicitud', 'Crimson');
    return rejectWithValue(message || data || 'Error');
  }
);

export const checkPendingSolicitud = createAsyncThunk(
  'solicitud/checkPendingSolicitud',
  async ({ tipo, idDiplomado }: { tipo?: string; idDiplomado?: number } = {}, { dispatch, getState, rejectWithValue }) => {
    const state = getState() as RootState;
    const { usuario } = resolveAuth(state);

    if (!usuario) {
      return rejectWithValue('Usuario no identificado');
    }

    const body: any = { idsp: 231};
    if (tipo) body.tipo = tipo;

    dispatch(setLoading(true));
    const { result, message, data } = await getData(body, getState, dispatch);
    dispatch(setLoading(false));

    if (result) {
      const parsed = typeof data === 'string' ? JSON.parse(data) : data;
      return parsed;
    }

    return rejectWithValue(message || 'No fue posible verificar solicitudes pendientes');
  }
);

export const checkDataCompletion = createAsyncThunk(
  'solicitud/checkDataCompletion',
  async (_, { dispatch, getState, rejectWithValue }) => {
    const body = { idsp: 230 };

    dispatch(setLoading(true));
    const { result, message, data } = await getData(body, getState, dispatch);
    dispatch(setLoading(false));

    if (result) {
      const parsed = typeof data === 'string' ? JSON.parse(data) : data;
      return parsed;
    }

    return rejectWithValue(message || 'No fue posible verificar completitud de datos');
  }
);

const solicitudSlice = createSlice({
  name: 'solicitudAlumno',
  initialState,
  reducers: {
    resetSolicitudState: () => initialState,
    clearPendingSolicitud: (state) => {
      state.pendingSolicitud = null;
      state.checkingPending = false;
    },
    clearDataCompletion: (state) => {
      state.dataCompletion = null;
      state.checkingCompletion = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPublicDiplomados.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchPublicDiplomados.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.diplomados = action.payload.items || [];
        state.totalDiplomados = action.payload.total || 0;
      })
      .addCase(fetchPublicDiplomados.rejected, (state, action) => {
        state.status = 'failed';
        state.error = (action.payload as string) || 'Error al obtener diplomados';
      })
      .addCase(fetchPublicCiclos.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchPublicCiclos.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.ciclos = action.payload.items || [];
        state.totalCiclos = action.payload.total || 0;
      })
      .addCase(fetchPublicCiclos.rejected, (state, action) => {
        state.status = 'failed';
        state.error = (action.payload as string) || 'Error al obtener ciclos';
      })
      .addCase(createSolicitudInscripcion.pending, (state) => {
        state.creating = true;
        state.error = null;
      })
      .addCase(createSolicitudInscripcion.fulfilled, (state, action) => {
        state.creating = false;
        state.lastMessage = action.payload?.message || 'Solicitud registrada';
      })
      .addCase(createSolicitudInscripcion.rejected, (state, action) => {
        state.creating = false;
        state.error = (action.payload as string) || 'Error al registrar solicitud';
      })
      .addCase(createSolicitudReinscripcion.pending, (state) => {
        state.creating = true;
        state.error = null;
      })
      .addCase(createSolicitudReinscripcion.fulfilled, (state, action) => {
        state.creating = false;
        state.lastMessage = action.payload?.message || 'Solicitud registrada';
      })
      .addCase(createSolicitudReinscripcion.rejected, (state, action) => {
        state.creating = false;
        state.error = (action.payload as string) || 'Error al registrar solicitud';
      })
      .addCase(checkPendingSolicitud.pending, (state) => {
        state.checkingPending = true;
        state.error = null;
      })
      .addCase(checkPendingSolicitud.fulfilled, (state, action) => {
        state.checkingPending = false;
        state.pendingSolicitud = action.payload;
      })
      .addCase(checkPendingSolicitud.rejected, (state, action) => {
        state.checkingPending = false;
        state.error = (action.payload as string) || 'Error al verificar solicitudes pendientes';
        state.pendingSolicitud = null;
      })
      .addCase(checkDataCompletion.pending, (state) => {
        state.checkingCompletion = true;
        state.error = null;
      })
      .addCase(checkDataCompletion.fulfilled, (state, action) => {
        state.checkingCompletion = false;
        state.dataCompletion = action.payload;
      })
      .addCase(checkDataCompletion.rejected, (state, action) => {
        state.checkingCompletion = false;
        state.error = (action.payload as string) || 'Error al verificar completitud de datos';
        state.dataCompletion = null;
      });
  },
});

export const { resetSolicitudState, clearPendingSolicitud, clearDataCompletion } = solicitudSlice.actions;

export const selectSolicitudState = (state: RootState) => state.alumnoSolicitud;
export const selectSolicitudDiplomados = (state: RootState) => state.alumnoSolicitud.diplomados;
export const selectSolicitudDiplomadosTotal = (state: RootState) => state.alumnoSolicitud.totalDiplomados;
export const selectSolicitudCiclos = (state: RootState) => state.alumnoSolicitud.ciclos;
export const selectSolicitudCreating = (state: RootState) => state.alumnoSolicitud.creating;
export const selectSolicitudError = (state: RootState) => state.alumnoSolicitud.error;
export const selectPendingSolicitud = (state: RootState) => state.alumnoSolicitud.pendingSolicitud;
export const selectCheckingPending = (state: RootState) => state.alumnoSolicitud.checkingPending;
export const selectDataCompletion = (state: RootState) => state.alumnoSolicitud.dataCompletion;
export const selectCheckingCompletion = (state: RootState) => state.alumnoSolicitud.checkingCompletion;

export default solicitudSlice.reducer;
